import api from './api';
import { isValidObjectId } from '../utils/mongoUtils';

export interface PhotoType {
  _id?: string;
  url: string;
  description?: string;
  uploadDate?: Date;
  uploadedBy?: string | {
    _id: string;
    displayName: string;
    photoURL?: string;
  };
}

export interface ServiceRequestType {
  _id?: string;
  number?: string;
  client: string | {
    _id: string;
    name: string;
    documentNumber: string;
  };
  asset: string | {
    _id: string;
    name: string;
    category?: string;
  };
  type: 'maintenance' | 'installation' | 'inspection' | 'repair' | 'other';
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'waiting' | 'in_progress' | 'completed' | 'cancelled';
  assignedTo?: string | {
    _id: string;
    displayName: string;
    photoURL?: string;
  };
  estimatedTime?: number;
  startDate?: Date;
  endDate?: Date;
  location?: {
    address?: string;
    latitude: number;
    longitude: number;
    updatedAt?: Date;
  };
  actions?: Array<{
    _id?: string;
    description: string;
    timestamp?: Date;
    performedBy?: string | {
      _id: string;
      displayName: string;
      photoURL?: string;
    };
    status?: 'info' | 'warning' | 'error' | 'success';
  }>;
  checklist?: Array<{
    _id?: string;
    description: string;
    isCompleted: boolean;
    completedAt?: Date;
    completedBy?: string | {
      _id: string;
      displayName: string;
      photoURL?: string;
    };
    notes?: string;
    requireSignature?: boolean;
    type?: 'simple_check' | 'image_capture' | 'update_data' | 'update_equipment' | 'collect_signature' | 'value_input';
    value?: string;
    executionOrder?: number;
    templateId?: string;
    templateName?: string;
    parentItemId?: string | null;
  }>;
  
  // Lista de templates de checklist associados ao tipo de chamado
  checklistTemplates?: Array<{
    _id: string;
    name: string;
    description?: string;
    serviceType: string;
    items: Array<{
      description: string;
      requireSignature: boolean;
      type: string;
      executionOrder: number;
      isActive: boolean;
    }>;
  }>;
  
  // Lista de itens de checklist sugeridos com base nos templates
  suggestedChecklist?: Array<{
    description: string;
    isCompleted: boolean;
    requireSignature: boolean;
    type: string;
    executionOrder: number;
    templateId: string;
    templateName: string;
    parentItemId?: string | null;
  }>;
  photos?: Array<{
    _id?: string;
    url: string;
    description?: string;
    uploadDate?: Date;
    uploadedBy?: string | {
      _id: string;
      displayName: string;
      photoURL?: string;
    };
  }>;
  signatures?: Array<{
    _id?: string;
    url: string;
    name: string;
    role?: string;
    timestamp?: Date;
  }>;
  notes?: string;
  sla?: string | {
    _id: string;
    name: string;
    responseTime: number;
    resolutionTime: number;
  };
  slaDetails?: {
    responseDeadline: Date;
    resolutionDeadline: Date;
    isOverdue: boolean;
  };
  createdBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ServiceRequestFilters {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  client?: string;
  asset?: string;
  type?: string;
  assignedTo?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isActive?: boolean;
  unassignedOnly?: string;
}

export interface KanbanData {
  waiting: ServiceRequestType[];
  in_progress: ServiceRequestType[];
  completed: ServiceRequestType[];
}

class ServiceRequestService {
  async createServiceRequest(data: ServiceRequestType): Promise<{ serviceRequest: ServiceRequestType; message: string }> {
    const response = await api.post('/service-requests', data);
    return response.data;
  }

  async getServiceRequests(filters: ServiceRequestFilters = {}): Promise<{
    docs: ServiceRequestType[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  }> {
    try {
      console.log('ServiceRequestService: Enviando requisição para /service-requests com filtros:', filters);
      
      // Tentar usar a API com paginação
      const response = await api.get('/service-requests', { params: filters });
      
      console.log('ServiceRequestService: Resposta recebida:', response);
      console.log('ServiceRequestService: Dados da resposta:', response.data);
      
      // Verificar se a resposta tem o formato esperado de paginação
      if (response.data && Array.isArray(response.data.docs)) {
        console.log('ServiceRequestService: Formato de paginação detectado, retornando dados');
        return response.data;
      }
      
      // Se a resposta não tiver o formato de paginação, mas for um array
      if (response.data && Array.isArray(response.data)) {
        // Criar um objeto de resposta no formato de paginação
        return {
          docs: response.data,
          totalDocs: response.data.length,
          limit: response.data.length,
          totalPages: 1,
          page: 1,
          pagingCounter: 1,
          hasPrevPage: false,
          hasNextPage: false,
          prevPage: null,
          nextPage: null
        };
      }
      
      // Se a resposta não for nem um objeto com docs nem um array
      console.warn('Resposta da API de chamados não tem o formato esperado:', response.data);
      return {
        docs: [],
        totalDocs: 0,
        limit: 10,
        totalPages: 0,
        page: 1,
        pagingCounter: 1,
        hasPrevPage: false,
        hasNextPage: false,
        prevPage: null,
        nextPage: null
      };
    } catch (error) {
      console.error('Erro ao buscar chamados:', error);
      // Retornar um objeto vazio no formato esperado
      return {
        docs: [],
        totalDocs: 0,
        limit: 10,
        totalPages: 0,
        page: 1,
        pagingCounter: 1,
        hasPrevPage: false,
        hasNextPage: false,
        prevPage: null,
        nextPage: null
      };
    }
  }

  async getServiceRequestsByStatus(assignedTo?: string): Promise<KanbanData> {
    const response = await api.get('/service-requests/kanban', {
      params: { assignedTo }
    });
    return response.data;
  }

  async getServiceRequestById(id: string): Promise<ServiceRequestType | { error: string, _id?: string, number?: string, description?: string, status?: string, location?: any }> {
    try {
      console.log(`Iniciando busca do chamado com ID: ${id}`);
      
      // Validar se o ID é um ObjectId válido do MongoDB
      if (!isValidObjectId(id)) {
        console.error(`ID inválido: ${id} não é um ObjectId válido do MongoDB`);
        return { 
          error: `ID inválido: ${id} não é um ObjectId válido do MongoDB`,
          _id: id, // Manter o ID para referência
          number: `ID: ${String(id).substring(0, 8)}...`,
          description: 'ID inválido',
          status: 'error'
        };
      }
      
      // Adicionar um timestamp para evitar cache
      const timestamp = new Date().getTime();
      console.log(`Enviando requisição para /service-requests/${id}?includeChecklist=true&_t=${timestamp}`);
      
      // Adicionar parâmetro includeChecklist=true para receber o checklist e timestamp para evitar cache
      const response = await api.get(`/service-requests/${id}?includeChecklist=true&_t=${timestamp}`);
      
      if (!response.data) {
        console.error(`Resposta vazia para o chamado ${id}`);
        return { 
          error: `Resposta vazia para o chamado ${id}`,
          _id: id,
          number: `ID: ${String(id).substring(0, 8)}...`,
          description: 'Resposta vazia',
          status: 'waiting'
        };
      }
      
      console.log(`Chamado ${id} carregado com sucesso:`, response.data);
      
      // Verificar se há ações no chamado
      if (response.data.actions && Array.isArray(response.data.actions)) {
        console.log(`Chamado possui ${response.data.actions.length} ações`);
        
        // Exibir as últimas ações
        if (response.data.actions.length > 0) {
          const lastAction = response.data.actions[response.data.actions.length - 1];
          console.log(`Última ação: ${lastAction.description} (${lastAction.status}) - ${new Date(lastAction.timestamp || lastAction.createdAt).toLocaleString()}`);
        }
      } else {
        console.log(`Chamado não possui ações ou o formato é inválido`);
      }
      
      // Verificar se os dados estão completos
      if (response.data) {
        console.log(`Tipo do _id: ${typeof response.data._id}`);
        console.log(`Valor do _id: ${response.data._id}`);
        console.log(`Tipo do number: ${typeof response.data.number}`);
        console.log(`Valor do number: ${response.data.number}`);
        console.log(`Tipo do status: ${typeof response.data.status}`);
        console.log(`Valor do status: ${response.data.status}`);
        console.log(`Tipo do description: ${typeof response.data.description}`);
        console.log(`Valor do description: ${response.data.description}`);
        
        if (response.data.location) {
          console.log(`Tipo do location.address: ${typeof response.data.location.address}`);
          console.log(`Valor do location.address: ${response.data.location.address}`);
        } else {
          console.log('location não definido');
        }
      }
      
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao buscar chamado técnico ${id}:`, error);
      console.error('Detalhes do erro:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      return { 
        error: error.response?.data?.message || error.message || 'Erro ao buscar chamado técnico',
        _id: id, // Manter o ID para referência
        number: `ID: ${String(id).substring(0, 8)}...`,
        description: 'Erro ao carregar chamado',
        status: 'waiting',
        location: {
          address: 'Endereço indisponível'
        }
      };
    }
  }

  async updateServiceRequest(id: string, data: Partial<ServiceRequestType>): Promise<{ serviceRequest: Partial<ServiceRequestType>; message: string }> {
    const response = await api.put(`/service-requests/${id}`, data);
    return response.data;
  }

  async addAction(id: string, description: string, status: 'info' | 'warning' | 'error' | 'success' = 'info'): Promise<{ action: any; message: string }> {
    const response = await api.post(`/service-requests/${id}/actions`, { description, status });
    return response.data;
  }

  async updateChecklist(id: string, checklist: ServiceRequestType['checklist']): Promise<{ checklist: ServiceRequestType['checklist']; message: string }> {
    console.log('[ServiceRequest.updateChecklist] Enviando checklist para o servidor:', checklist);
    
    // Verificar se há itens com valor definido
    if (checklist) {
      // Verificar se há itens com parentItemId
      const itemsWithParentId = checklist.filter(item => item.parentItemId);
      console.log(`[ServiceRequest.updateChecklist] Itens com parentItemId: ${itemsWithParentId.length}`);
      
      if (itemsWithParentId.length > 0) {
        console.log('[ServiceRequest.updateChecklist] Detalhes dos itens com parentItemId:');
        itemsWithParentId.forEach((item, idx) => {
          console.log(`Item ${idx}: ${item.description}, parentItemId: ${item.parentItemId}`);
        });
      }
      
      checklist.forEach((item, index) => {
        console.log(`[ServiceRequest.updateChecklist] Item ${index}:`, {
          description: item.description,
          type: item.type,
          value: item.value,
          isCompleted: item.isCompleted,
          parentItemId: item.parentItemId || null
        });
      });
    }
    
    const response = await api.put(`/service-requests/${id}/checklist`, { checklist });
    console.log('[ServiceRequest.updateChecklist] Resposta do servidor:', response.data);
    return response.data;
  }
  
  async initializeChecklist(id: string): Promise<{ checklist: ServiceRequestType['checklist']; message: string }> {
    console.log(`[initializeChecklist] Inicializando checklist para o chamado ${id}`);
    
    try {
      // Enviar um array vazio para o backend inicializar o checklist com os templates
      const response = await api.put(`/service-requests/${id}/checklist`, { checklist: [] });
      
      console.log(`[initializeChecklist] Resposta recebida do backend:`, response.data);
      console.log(`[initializeChecklist] Checklist recebido:`, response.data.checklist);
      console.log(`[initializeChecklist] Número de itens recebidos:`, response.data.checklist ? response.data.checklist.length : 0);
      
      return response.data;
    } catch (error) {
      console.error(`[initializeChecklist] Erro ao inicializar checklist:`, error);
      throw error;
    }
  }
  
  // Método alternativo para aplicar templates de checklist diretamente
  async applyChecklistTemplatesDirect(id: string, serviceType: string): Promise<{ checklist: ServiceRequestType['checklist']; message: string }> {
    console.log(`[applyChecklistTemplatesDirect] Aplicando templates para o chamado ${id} do tipo ${serviceType}`);
    
    try {
      // Primeiro, buscar os templates disponíveis para o tipo de serviço
      const templatesResponse = await api.get(`/checklist-templates/service-type/${serviceType}`);
      
      // Verificar a estrutura da resposta
      console.log(`[applyChecklistTemplatesDirect] Resposta completa:`, templatesResponse);
      console.log(`[applyChecklistTemplatesDirect] Dados da resposta:`, templatesResponse.data);
      
      // Acessar o array de templates na propriedade 'checklistTemplates'
      const templatesArray = templatesResponse.data.checklistTemplates || [];
      
      console.log(`[applyChecklistTemplatesDirect] Templates encontrados:`, templatesArray);
      
      if (!templatesArray || templatesArray.length === 0) {
        console.log(`[applyChecklistTemplatesDirect] Nenhum template encontrado para o tipo ${serviceType}`);
        return { checklist: [], message: `Nenhum template disponível para o tipo ${serviceType}` };
      }
      
      // Criar itens de checklist a partir dos templates
      const checklistItems: ServiceRequestType['checklist'] = [];
      
      templatesArray.forEach((template: any) => {
        console.log(`[applyChecklistTemplatesDirect] Processando template:`, template);
        
        if (template && template.items && Array.isArray(template.items)) {
          template.items.forEach((item: any) => {
            if (item.isActive) {
              checklistItems.push({
                description: item.description,
                isCompleted: false,
                requireSignature: item.requireSignature,
                type: item.type,
                executionOrder: item.executionOrder,
                templateId: template._id,
                templateName: template.name,
                parentItemId: item.parentItemId || null
              });
            }
          });
        } else {
          console.log(`[applyChecklistTemplatesDirect] Template não tem itens válidos:`, template);
        }
      });
      
      // Ordenar itens por ordem de execução
      checklistItems.sort((a, b) => (a.executionOrder || 0) - (b.executionOrder || 0));
      
      console.log(`[applyChecklistTemplatesDirect] Itens de checklist criados:`, checklistItems);
      
      if (checklistItems.length === 0) {
        console.log(`[applyChecklistTemplatesDirect] Nenhum item de checklist criado`);
        return { checklist: [], message: 'Nenhum item de checklist disponível' };
      }
      
      // Atualizar o checklist do chamado com os itens criados
      const updateResponse = await api.put(`/service-requests/${id}/checklist`, { checklist: checklistItems });
      
      console.log(`[applyChecklistTemplatesDirect] Resposta da atualização:`, updateResponse.data);
      
      return updateResponse.data;
    } catch (error) {
      console.error(`[applyChecklistTemplatesDirect] Erro ao aplicar templates:`, error);
      throw error;
    }
  }

  async updateLocation(id: string, location: { address?: string; latitude: number; longitude: number }): Promise<{ location: ServiceRequestType['location']; message: string }> {
    const response = await api.put(`/service-requests/${id}/location`, location);
    return response.data;
  }

  async addPhotosToServiceRequest(id: string, photos: Array<{ url: string; description?: string }>): Promise<{ photos: PhotoType[]; message: string }> {
    console.log(`Adicionando ${photos.length} foto(s) ao chamado ${id}`);
    const response = await api.post(`/service-requests/${id}/photos`, { photos });
    console.log('Resposta da adição de fotos:', response.data);
    return response.data;
  }
  
  // Remover uma foto de um chamado técnico
  async removePhotoFromServiceRequest(serviceRequestId: string, photoId: string): Promise<{ photos: PhotoType[]; message: string }> {
    console.log(`Removendo foto ${photoId} do chamado ${serviceRequestId}`);
    const response = await api.delete(`/service-requests/${serviceRequestId}/photos/${photoId}`);
    console.log('Resposta da remoção de foto:', response.data);
    return response.data;
  }

  async addSignatures(id: string, signatures: Array<{ url: string; name: string; role?: string }>): Promise<{ signatures: ServiceRequestType['signatures']; message: string }> {
    const response = await api.post(`/service-requests/${id}/signatures`, { signatures });
    return response.data;
  }

  async deleteServiceRequest(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/service-requests/${id}`);
    return response.data;
  }

  // Upload de foto
  async uploadPhoto(file: File, description?: string): Promise<{ url: string; fileName: string; description?: string }> {
    try {
      console.log(`Iniciando upload de foto: ${file.name}, ${file.size} bytes`);
      
      // Criar FormData para enviar o arquivo
      const formData = new FormData();
      formData.append('file', file);
      
      if (description) {
        formData.append('description', description);
      }
      
      const response = await api.post('/upload/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Resposta do upload:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Erro ao fazer upload de foto:', error);
      throw error;
    }
  }
}

export default new ServiceRequestService();
